import Phaser from 'phaser';
import { BIOMES, type BiomeId } from '../../content/biomes';
import { BOOST_BY_ID } from '../../content/boosts';
import {
  CLEARED_COINS,
  CLEARED_COSMETIC,
  COSMETICS,
  GRAND_COINS,
  GRAND_COSMETIC,
  YARDS_COINS,
  YARDS_COSMETIC,
} from '../../content/cosmetics';
import { MOWER_DEFS, MOWERS, type MowerId } from '../../content/mowers';
import { TOOL_BY_ID } from '../../content/tools';
import {
  campaignLevelId,
  HEADLINE_UPGRADES,
  isEquipped,
  toggleCosmetic,
  UPGRADES,
  type UpgradeKind,
} from '../../meta/economy';
import type { Reveal } from '../../meta/reveals';
import { STAR, STAR_BITS } from '../../meta/save/schema';
import { haptics } from '../../platform/haptics';
import { gameAudio } from '../audio/audio';
import { isPaint, ownMowerTexture, paintedMowerTexture } from '../render/cosmeticArt';
import { saves } from '../records';
import { builderPrice } from '../../meta/customLawns';
import { Tile } from '../../sim/grid';
import { Dir } from '../../sim/direction';
import { TILE_SIZE } from '../layout';
import { PICKUP_FRAME, PICKUPS_KEY, TILES_KEY } from '../render/placeholderArt';
import { PROP_LIFT, propFrame, PROPS_KEY } from '../render/props';
import { frameIndex, tileFrame } from '../render/tileFrames';
import { bevelButton, drawBigStar } from '../ui/chrome';
import { reducedMotion } from '../ui/motion';
import { itemPreview, mowerPreview, PREVIEW_PATCH, stripePreview } from '../ui/showcase';
import { label, UI } from '../ui/text';
import { drawCoin } from '../ui/widgets';
import { wrapLineCount } from '../ui/wrap';
import {
  drawBossBadge,
  drawNeighborhood,
  drawTrophy,
  drawWorldIcon,
  TROPHY_SIZE,
} from '../ui/zoneArt';
import { bossFor } from '../../gen/boss';
import { FREE_LAWNS } from '../../meta/unlock';
import type { GarageOpen } from './GarageScene';

/**
 * Where the reveals end up: back to what the player chose, the Garage to see an unlock, or the
 * full game's offer (from the free part's last NEXT ZONE card).
 */
export type RevealExit = { garage: GarageOpen } | { unlock: true } | null;

export interface RevealData {
  reveals: Reveal[];
  /** Called once the last card is dismissed. */
  done: (exit: RevealExit) => void;
}

/** One card's content: a big title, a name, the hero picture, a few lines, its buttons. */
interface Card {
  title: string;
  name?: string;
  nameColor?: number;
  /** Draws the hero centered at (cx, cy); returns the hero's half height. */
  hero: (cx: number, cy: number) => number;
  lines: { text: string; color?: number }[];
  /** The primary (bottom) button's label; the secondary sits above it. */
  primary: string;
  secondary?: { label: string; action: () => void };
  big?: boolean;
  fireworks?: boolean;
}

const GOLD = 0xffd23f;
const GOLD_DARK = 0xc07a00;
const GOLD_LIGHT = 0xfff1a8;

/**
 * Celebration cards after a lawn (owner, 2026-10-05: excite and praise the player's progress),
 * played one after another over everything: a zone finished (its trophy, then the next zone's
 * neighborhood sliding in with what is new there), every star in a zone, a rare find unwrapped,
 * a mower, tool, boost or upgrade unlocked, a milestone. Light rays turn behind the hero, a chime
 * rings, fireworks for the big ones; reduced motion keeps it still.
 */
export class RevealScene extends Phaser.Scene {
  private data_!: RevealData;
  private queue: Card[] = [];
  private exit: RevealExit = null;

  constructor() {
    super('Reveal');
  }

  init(data: RevealData): void {
    this.data_ = data;
    this.exit = null;
  }

  create(): void {
    this.queue = this.data_.reveals.flatMap((r) => this.cards(r));
    this.showNext();
  }

  private showNext(): void {
    for (const o of [...this.children.list]) o.destroy();
    this.tweens.killAll();
    this.time.removeAllEvents();
    const card = this.queue.shift();
    if (!card) {
      this.scene.stop();
      this.data_.done(this.exit);
      return;
    }
    this.drawCard(card);
  }

  // ── Layout ───────────────────────────────────────────────────────────────────────────────

  private drawCard(card: Card): void {
    const { width, height } = this.scale.gameSize;
    const still = reducedMotion();
    // The backdrop swallows taps, so nothing underneath is pressed.
    this.add
      .rectangle(0, 0, width, height, 0x0b0820, 0.94)
      .setOrigin(0)
      .setDepth(-2)
      .setInteractive();

    // Title: a stamp, big.
    const titleY = Math.round(height * 0.08);
    const scale = card.title.length * 12 - 2 <= width - 16 ? 2 : 1;
    const title = label(
      this,
      width / 2,
      titleY,
      card.title,
      scale,
      card.big ? GOLD : UI.accent,
    ).setOrigin(0.5, 0);
    let y = titleY + (scale === 2 ? 20 : 12);
    if (card.name) {
      label(this, width / 2, y, card.name, 1, card.nameColor ?? UI.text)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 20)
        .setCenterAlign();
      y += wrapLineCount(card.name, width - 20) * 10;
    }
    // The hero sits under the name, a little above the middle.
    const heroY = Math.max(Math.round(height * 0.4), y + 44);
    this.rays(width / 2, heroY, card.big ? GOLD : UI.panelHi, still);
    if (!still) {
      title.setScale(2.4 * (scale / 2)).setAlpha(0);
      this.tweens.add({ targets: title, scale: 1, alpha: 1, duration: 360, ease: 'Back.easeOut' });
    }

    const half = card.hero(width / 2, heroY);
    let ly = heroY + half + 12;
    for (const line of card.lines) {
      label(this, width / 2, ly, line.text, 1, line.color ?? UI.text)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 24)
        .setCenterAlign();
      ly += wrapLineCount(line.text, width - 24) * 10 + 2;
    }

    // Buttons at the bottom, in thumb reach.
    const bw = width - 24;
    const by = height - 40;
    const big = card.primary.length * 12 + 8 <= bw;
    bevelButton(this, 12, by, bw, 26, card.primary, () => this.showNext(), true, big ? 2 : 1);
    if (card.secondary) {
      const s = card.secondary;
      bevelButton(this, 12, by - 26, bw, 20, s.label, () => {
        s.action();
        this.showNext();
      });
    }

    gameAudio.chime(card.big);
    haptics.impact();
    if (card.fireworks && !still) this.fireworks(width, heroY);
  }

  /** Light rays turning slowly behind the hero. */
  private rays(cx: number, cy: number, color: number, still: boolean): void {
    const g = this.add.graphics({ x: cx, y: cy }).setDepth(-1).setBlendMode(Phaser.BlendModes.ADD);
    const n = 12;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const b = a + Math.PI / n / 1.6;
      g.fillStyle(color, 0.12);
      g.fillTriangle(
        0,
        0,
        Math.cos(a) * 160,
        Math.sin(a) * 160,
        Math.cos(b) * 160,
        Math.sin(b) * 160,
      );
    }
    if (!still) this.tweens.add({ targets: g, angle: 360, duration: 24000, repeat: -1 });
  }

  /** Bursts of sparks over the top half, one after another. */
  private fireworks(width: number, heroY: number): void {
    const colors = [GOLD, 0xffffff, 0x7ad04c, 0xff8a1f, 0x8fd3ff, 0xff9ec7];
    const burst = () => {
      const x = 20 + Math.random() * (width - 40);
      const y = 20 + Math.random() * (heroY - 10);
      const c = colors[Math.floor(Math.random() * colors.length)]!;
      for (let i = 0; i < 14; i++) {
        const a = (i / 14) * Math.PI * 2;
        const d = 14 + Math.random() * 12;
        const s = this.add.graphics({ x, y }).setDepth(-1);
        s.fillStyle(c).fillRect(-1, -1, 2, 2);
        this.tweens.add({
          targets: s,
          x: x + Math.cos(a) * d,
          y: y + Math.sin(a) * d + 6,
          alpha: 0,
          duration: 700 + Math.random() * 200,
          ease: 'Quad.easeOut',
          onComplete: () => s.destroy(),
        });
      }
      gameAudio.play('pickup', 1.4 + Math.random() * 0.4);
    };
    for (let i = 0; i < 6; i++) this.time.delayedCall(250 + i * 330, burst);
    this.time.addEvent({ delay: 1600, loop: true, callback: burst });
  }

  // ── Cards ────────────────────────────────────────────────────────────────────────────────

  private cards(r: Reveal): Card[] {
    switch (r.kind) {
      case 'zoneComplete':
        return [this.zoneCard(r.zone), this.nextZoneCard(r.next)];
      case 'allStars':
        return [this.allStarsCard(r.reward.zone, r.reward.cosmetic, r.reward.coins)];
      case 'everyStar':
        return [this.everyStarCard()];
      case 'everyYard':
        return [this.everyYardCard()];
      case 'everyLawn':
        return [this.everyLawnCard()];
      case 'rare':
        return [this.rareCard(r.item)];
      case 'critterTag':
        return [this.critterCard(r.critter, r.item)];
      case 'shareGift':
        return [this.shareGiftCard(r.coins)];
      case 'shareReward':
        return [this.shareRewardCard(r.item, r.shares)];
      case 'mower': {
        const def = MOWER_DEFS[r.id];
        return [
          this.unlockCard(
            'NEW MOWER!',
            def.name.toUpperCase(),
            (cx, cy) => {
              mowerPreview(
                this,
                cx - PREVIEW_PATCH.width / 2,
                cy - PREVIEW_PATCH.height / 2,
                PREVIEW_PATCH.width,
                r.id,
                {
                  texture: ownMowerTexture(this, r.id, saves().save),
                  lines: [],
                },
              );
              return this.frame(cx, cy);
            },
            [MOWERS[r.id].blurb, `In the Garage: ${def.price} coins`],
            { tab: 'mowers', id: r.id },
          ),
        ];
      }
      case 'tool': {
        const def = TOOL_BY_ID[r.id];
        return [
          this.unlockCard(
            'NEW TOOL!',
            def.name.toUpperCase(),
            (cx, cy) => this.itemHero(cx, cy, r.id),
            [`In the Garage: ${def.price} coins`, 'Equip it before a lawn.'],
            { tab: 'tools', id: r.id },
          ),
        ];
      }
      case 'boost': {
        const def = BOOST_BY_ID[r.id];
        return [
          this.unlockCard(
            'NEW BOOST!',
            def.name.toUpperCase(),
            (cx, cy) => this.itemHero(cx, cy, r.id),
            [def.blurb, `In the Garage: ${def.price} coins`],
            { tab: 'boosts', id: r.id },
          ),
        ];
      }
      case 'upgrade':
        return [
          this.unlockCard(
            'NEW UPGRADE!',
            UPGRADES[r.id].name.toUpperCase(),
            (cx, cy) => this.itemHero(cx, cy, r.id),
            [UPGRADES[r.id].blurb, 'For every mower, in the Garage.'].filter(
              (line): line is string => line !== undefined,
            ),
            { tab: 'mowers', id: saves().save.selectedMower },
          ),
        ];
      case 'upgradeLevels': {
        // A zone's new mastery levels: one card listing them (its headline first).
        const save = saves().save;
        const sorted = [...r.levels].sort(
          (a, b) => Number(isHeadline(b.id, b.level)) - Number(isHeadline(a.id, a.level)),
        );
        const top = sorted[0]!;
        return [
          this.unlockCard(
            isHeadline(top.id, top.level) ? 'BIG GOAL!' : 'NEW LEVELS!',
            `${UPGRADES[top.id].name.toUpperCase()} ${roman(top.level + 1)}`,
            (cx, cy) => this.itemHero(cx, cy, top.id),
            [
              ...sorted.slice(1).map((l) => `${UPGRADES[l.id].name} ${roman(l.level + 1)}`),
              'For every mower, in the Garage.',
            ],
            { tab: 'mowers', id: save.selectedMower },
          ),
        ];
      }
      case 'toolUpgrades': {
        // Tool upgrades opening: the ones for tools the player has (none: no card).
        const save = saves().save;
        const owned = r.levels.filter((l) => save.tools.owned.includes(l.id));
        const first = owned[0];
        if (!first) return [];
        return [
          this.unlockCard(
            'TOOL UPGRADES!',
            TOOL_BY_ID[first.id].name.toUpperCase(),
            (cx, cy) => this.itemHero(cx, cy, first.id),
            owned
              .slice(0, 4)
              .map((l) => `${TOOL_BY_ID[l.id].name}: ${TOOL_BY_ID[l.id].upgrades[l.level]!.label}`),
            { tab: 'tools', id: first.id },
          ),
        ];
      }
      case 'builder': {
        const price = builderPrice(saves().save);
        return [
          {
            title: 'LAWN BUILDER!',
            name: 'MAKE YOUR OWN LAWNS',
            nameColor: UI.accent,
            hero: (cx, cy) => this.builderHero(cx, cy),
            lines: [
              { text: 'Paint them, check them, play them.', color: UI.text },
              { text: price !== null ? `In RUNS: ${price} coins` : 'In RUNS.', color: UI.dim },
            ],
            primary: 'CONTINUE',
          },
        ];
      }
      case 'milestone':
        return [
          {
            title: 'MILESTONE!',
            name: r.title,
            nameColor: GOLD,
            hero: (cx, cy) => this.rosette(cx, cy),
            lines: [{ text: r.detail, color: UI.dim }],
            primary: 'NICE!',
          },
        ];
    }
  }

  /** A lawn-patch hero's frame (it is drawn by the showcase helpers); returns its half height. */
  private frame(cx: number, cy: number): number {
    const g = this.add.graphics();
    g.lineStyle(1, UI.rule).strokeRect(
      cx - PREVIEW_PATCH.width / 2 - 1.5,
      cy - PREVIEW_PATCH.height / 2 - 1.5,
      PREVIEW_PATCH.width + 3,
      PREVIEW_PATCH.height + 3,
    );
    return PREVIEW_PATCH.height / 2;
  }

  /** The builder's hero: a little lawn being made (grass, a flower bed, a rock, a coin). */
  private builderHero(cx: number, cy: number): number {
    const w = PREVIEW_PATCH.width;
    const h = PREVIEW_PATCH.height;
    const x0 = Math.round(cx - w / 2);
    const y0 = Math.round(cy - h / 2);
    const cols = Math.ceil(w / TILE_SIZE);
    const rows = Math.ceil(h / TILE_SIZE);
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const bed = y === rows - 2 && x >= 1 && x <= 3;
        this.add
          .image(
            x0 + x * TILE_SIZE,
            y0 + y * TILE_SIZE,
            TILES_KEY,
            bed ? frameIndex('flowerBed') : tileFrame(Tile.Grass, Dir.N, 'grass', x, y),
          )
          .setOrigin(0)
          .setCrop(
            0,
            0,
            Math.min(TILE_SIZE, w - x * TILE_SIZE),
            Math.min(TILE_SIZE, h - y * TILE_SIZE),
          );
      }
    }
    this.add
      .image(x0 + 2.5 * TILE_SIZE, y0 + 2 * TILE_SIZE - PROP_LIFT, PROPS_KEY, propFrame('rock'))
      .setOrigin(0.5, 1);
    this.add.image(
      x0 + (cols - 1.5) * TILE_SIZE,
      y0 + 1.5 * TILE_SIZE,
      PICKUPS_KEY,
      PICKUP_FRAME.coin,
    );
    return this.frame(cx, cy);
  }

  private itemHero(cx: number, cy: number, icon: Parameters<typeof itemPreview>[4]): number {
    itemPreview(
      this,
      cx - PREVIEW_PATCH.width / 2,
      cy - PREVIEW_PATCH.height / 2,
      PREVIEW_PATCH.width,
      icon,
      [],
    );
    return this.frame(cx, cy);
  }

  private unlockCard(
    title: string,
    name: string,
    hero: Card['hero'],
    lines: string[],
    open: GarageOpen,
  ): Card {
    return {
      title,
      name,
      nameColor: UI.accent,
      hero: (cx, cy) => {
        // The new thing slides in from the side.
        const mark = this.children.length;
        const half = hero(cx, cy);
        if (!reducedMotion()) {
          for (const o of this.children.list.slice(mark)) {
            const t = o as unknown as { x: number; alpha: number };
            const x = t.x;
            t.x = x + 140;
            this.tweens.add({ targets: t, x, duration: 420, ease: 'Back.easeOut', delay: 150 });
          }
        }
        return half;
      },
      lines: lines.map((text, i) => ({ text, color: i === 0 ? UI.text : UI.dim })),
      primary: 'CONTINUE',
      secondary: { label: 'SEE IT IN THE GARAGE', action: () => (this.exit = { garage: open }) },
    };
  }

  /** ZONE COMPLETE: the zone's trophy, its lawns and stars. */
  private zoneCard(zone: BiomeId): Card {
    const def = BIOMES.find((b) => b.id === zone)!;
    const save = saves().save;
    let stars = 0;
    let lawns = 0;
    for (let n = def.levels[0]; n <= def.levels[1]; n++) {
      const bits = save.levels[campaignLevelId(n)]?.stars ?? 0;
      if (bits & STAR.clear) lawns++;
      stars += STAR_BITS.filter((b) => bits & b).length;
    }
    const total = def.levels[1] - def.levels[0] + 1;
    return {
      title: 'ZONE COMPLETE!',
      name: def.name.toUpperCase(),
      nameColor: GOLD,
      big: true,
      fireworks: true,
      hero: (cx, cy) => this.trophy(cx, cy, zone, def.levels[1]),
      lines: [
        { text: `Lawns ${lawns}/${total}   Stars ${stars}/${total * 3}` },
        {
          text: stars < total * 3 ? 'Every star here earns a prize.' : 'Every star, too. Superb!',
          color: UI.dim,
        },
      ],
      primary: 'ONWARD!',
    };
  }

  /** NEXT ZONE: its neighborhood slides in, with what is new there. The last zone: game done. */
  private nextZoneCard(next: BiomeId | null): Card {
    if (!next) {
      return {
        title: 'ALL MOWED!',
        name: 'Every zone, every lawn.',
        nameColor: GOLD,
        big: true,
        fireworks: true,
        hero: (cx, cy) => {
          // Every zone's icon in a row, twice size.
          const step = 10;
          const g = this.add.graphics({ x: Math.round(cx - BIOMES.length * step), y: cy - 9 });
          BIOMES.forEach((b, i) => drawWorldIcon(g, b.id, i * step, 0));
          g.setScale(2);
          return 12;
        },
        lines: [
          { text: 'The neighborhood has never looked so good.' },
          { text: 'Endless runs and the daily lawn await.', color: UI.dim },
        ],
        primary: 'TAKE A BOW',
      };
    }
    const def = BIOMES.find((b) => b.id === next)!;
    const { width } = this.scale.gameSize;
    const paid = def.levels[0] > FREE_LAWNS && !saves().save.fullGame;
    return {
      title: 'NEXT ZONE',
      name: def.name.toUpperCase(),
      nameColor: GOLD,
      hero: (_cx, cy) => {
        const h = 54;
        const mark = this.children.length;
        drawNeighborhood(this, cy - h / 2, h, next);
        const g = this.add.graphics();
        g.lineStyle(1, UI.rule).strokeRect(-0.5, cy - h / 2 - 0.5, width + 1, h + 1);
        if (!reducedMotion()) {
          for (const o of this.children.list.slice(mark)) {
            const t = o as unknown as { x: number };
            const x = t.x;
            t.x = x + width;
            this.tweens.add({ targets: t, x, duration: 600, ease: 'Cubic.easeOut', delay: 120 });
          }
        }
        return h / 2;
      },
      lines: [
        { text: "What's new:", color: UI.dim },
        ...def.teaser.map((t) => ({ text: `- ${t}` })),
        ...(paid ? [{ text: 'In the full game.', color: UI.accent }] : []),
      ],
      // The free part ends here: the full game's offer, once, at a high point (never mid-play).
      ...(paid
        ? {
            primary: 'NOT NOW',
            secondary: {
              label: 'UNLOCK THE FULL GAME',
              action: () => (this.exit = { unlock: true }),
            },
          }
        : { primary: `ON TO ${shortName(def.name)}!` }),
    };
  }

  /** ALL STARS: a gold medal over the zone's prize, shown on the player's mower or lawn. */
  private allStarsCard(zone: BiomeId, cosmetic: string, coins: number): Card {
    const def = BIOMES.find((b) => b.id === zone)!;
    const name = COSMETICS.find((c) => c.id === cosmetic)?.name ?? '';
    return {
      title: 'ALL STARS!',
      name: def.name.toUpperCase(),
      nameColor: GOLD,
      big: true,
      fireworks: true,
      hero: (cx, cy) => {
        const half = this.cosmeticHero(cx, cy, cosmetic);
        this.medal(cx + PREVIEW_PATCH.width / 2 - 2, cy - PREVIEW_PATCH.height / 2 + 2);
        return half;
      },
      lines: [
        { text: `Prize: ${name}`, color: UI.accent },
        { text: `And ${coins} coins.`, color: UI.dim },
      ],
      primary: 'WONDERFUL!',
      secondary: this.wearButton(cosmetic),
    };
  }

  /**
   * EVERY STAR (owner, 2026-10-29): every star on every lawn of the campaign. The prize, Stellar
   * Tie-Dye, turns and twinkles on the mower shown.
   */
  private everyStarCard(): Card {
    return {
      title: 'EVERY STAR!',
      name: 'STELLAR TIE-DYE',
      nameColor: GOLD,
      big: true,
      fireworks: true,
      hero: (cx, cy) => {
        // (The Reel Mower's paint is a small badge: the Push Mower shows the tie-dye off.)
        const mower = saves().save.selectedMower;
        const half = this.cosmeticHero(cx, cy, GRAND_COSMETIC, mower === 'reel' ? 'push' : mower);
        this.medal(cx + PREVIEW_PATCH.width / 2 - 2, cy - PREVIEW_PATCH.height / 2 + 2);
        return half;
      },
      lines: [
        { text: 'Every star on every lawn!', color: UI.accent },
        { text: `And ${GRAND_COINS} coins.`, color: UI.dim },
      ],
      primary: 'AMAZING!',
      secondary: this.wearButton(GRAND_COSMETIC),
    };
  }

  /**
   * EVERY YARD (owner, 2026-10-30): the last secret yard found. The prize, Secret Garden, blooms
   * and glints on the mower shown.
   */
  private everyYardCard(): Card {
    return {
      title: 'EVERY YARD!',
      name: 'SECRET GARDEN',
      nameColor: GOLD,
      big: true,
      fireworks: true,
      hero: (cx, cy) => {
        // (The Reel Mower's paint is a small badge: the Push Mower shows the garden off.)
        const mower = saves().save.selectedMower;
        const half = this.cosmeticHero(cx, cy, YARDS_COSMETIC, mower === 'reel' ? 'push' : mower);
        this.medal(cx + PREVIEW_PATCH.width / 2 - 2, cy - PREVIEW_PATCH.height / 2 + 2);
        return half;
      },
      lines: [
        { text: 'Every secret yard found!', color: UI.accent },
        { text: `And ${YARDS_COINS} coins.`, color: UI.dim },
      ],
      primary: 'WONDERFUL!',
      secondary: this.wearButton(YARDS_COSMETIC),
    };
  }

  /**
   * EVERY LAWN (owner, 2026-11-04): every campaign lawn mowed, stars or not. The prize, Finish
   * Line Paint, on the mower shown.
   */
  private everyLawnCard(): Card {
    return {
      title: 'EVERY LAWN!',
      name: 'FINISH LINE PAINT',
      nameColor: GOLD,
      big: true,
      fireworks: true,
      hero: (cx, cy) => {
        // (The Reel Mower's paint is a small badge: the Push Mower shows the checks off.)
        const mower = saves().save.selectedMower;
        const half = this.cosmeticHero(cx, cy, CLEARED_COSMETIC, mower === 'reel' ? 'push' : mower);
        this.medal(cx + PREVIEW_PATCH.width / 2 - 2, cy - PREVIEW_PATCH.height / 2 + 2);
        return half;
      },
      lines: [
        { text: 'Every lawn in the game mowed!', color: UI.accent },
        { text: `And ${CLEARED_COINS} coins.`, color: UI.dim },
      ],
      primary: 'WONDERFUL!',
      secondary: this.wearButton(CLEARED_COSMETIC),
    };
  }

  /** RARE FIND: a gift box shakes, pops open, and the find is shown. */
  private rareCard(item: string): Card {
    const name = COSMETICS.find((c) => c.id === item)?.name ?? '';
    return {
      title: 'RARE FIND!',
      name: name.toUpperCase(),
      nameColor: UI.accent,
      hero: (cx, cy) => {
        const mark = this.children.length;
        const half = this.cosmeticHero(cx, cy, item);
        const prize = this.children.list.slice(mark);
        if (reducedMotion()) return half;
        for (const o of prize)
          (o as unknown as { setVisible: (v: boolean) => void }).setVisible(false);
        const box = this.giftBox(cx, cy);
        this.tweens.add({
          targets: box,
          angle: { from: -8, to: 8 },
          duration: 90,
          yoyo: true,
          repeat: 5,
          onComplete: () => {
            gameAudio.play('rare');
            box.destroy();
            for (const o of prize)
              (o as unknown as { setVisible: (v: boolean) => void }).setVisible(true);
          },
        });
        return half;
      },
      lines: [{ text: 'Yours to keep!' }],
      primary: 'LOVELY!',
      secondary: this.wearButton(item),
    };
  }

  /** The first squirrel or rabbit tagged: its cosmetic, no gift box (it was earned, not found). */
  private critterCard(critter: string, item: string): Card {
    const name = COSMETICS.find((c) => c.id === item)?.name ?? '';
    return {
      title: 'TAG!',
      name: name.toUpperCase(),
      nameColor: UI.accent,
      hero: (cx, cy) => {
        gameAudio.play('rare');
        return this.cosmeticHero(cx, cy, item);
      },
      lines: [
        { text: `You tagged a ${critter}!` },
        { text: 'It hopped off, unharmed.', color: UI.dim },
      ],
      primary: 'HOORAY!',
      secondary: this.wearButton(item),
    };
  }

  /** The first share of the game: a heap of coins, a thank-you. */
  private shareGiftCard(coins: number): Card {
    return {
      title: 'THANK YOU!',
      name: `${coins} COINS`,
      nameColor: GOLD,
      hero: (cx, cy) => {
        gameAudio.play('rare');
        return this.coinHeap(cx, cy);
      },
      lines: [
        { text: 'For telling a friend about lawnmower.' },
        { text: 'Share it 5 times for a prize.', color: UI.dim },
      ],
      primary: 'THANKS!',
    };
  }

  /** The share that reached the goal: each of its cosmetics. */
  private shareRewardCard(item: string, shares: number): Card {
    const name = COSMETICS.find((c) => c.id === item)?.name ?? '';
    return {
      title: 'SHOUT-OUT!',
      name: name.toUpperCase(),
      nameColor: UI.accent,
      big: true,
      fireworks: true,
      hero: (cx, cy) => this.cosmeticHero(cx, cy, item),
      lines: [
        { text: `For sharing the game ${shares} times.` },
        { text: 'Thank you!', color: UI.dim },
      ],
      primary: 'HOORAY!',
      secondary: this.wearButton(item),
    };
  }

  /**
   * WEAR IT (a paint, on the mower in use) or MOW WITH IT (a stripe pattern): puts on a cosmetic
   * not already in use.
   */
  private wearButton(cosmetic: string): Card['secondary'] {
    const save = saves().save;
    if (isEquipped(save, cosmetic, save.selectedMower)) return undefined;
    return {
      label: isPaint(cosmetic) ? 'WEAR IT' : 'MOW WITH IT',
      action: () => saves().update((s) => toggleCosmetic(s, cosmetic, s.selectedMower)),
    };
  }

  private cosmeticHero(
    cx: number,
    cy: number,
    cosmetic: string,
    mower: MowerId = saves().save.selectedMower,
  ): number {
    const x = cx - PREVIEW_PATCH.width / 2;
    const y = cy - PREVIEW_PATCH.height / 2;
    if (isPaint(cosmetic)) {
      mowerPreview(this, x, y, PREVIEW_PATCH.width, mower, {
        texture: paintedMowerTexture(this, mower, cosmetic),
        lines: [],
      });
    } else {
      stripePreview(this, x, y, PREVIEW_PATCH.width, cosmetic, []);
    }
    return this.frame(cx, cy);
  }

  // ── Art ──────────────────────────────────────────────────────────────────────────────────

  /** A big gold cup with the zone's icon on its base and the boss's badge on the cup. */
  private trophy(cx: number, cy: number, zone: BiomeId, bossLevel: number): number {
    const k = 4;
    const w = TROPHY_SIZE.w * k;
    const h = TROPHY_SIZE.h * k;
    const g = this.add.graphics({ x: -Math.round(w / 2), y: -Math.round(h / 2) });
    const cup = this.add.container(Math.round(cx), Math.round(cy), [g]);
    drawTrophy(g, 0, 0, k, true);
    // The boss's badge on the cup, the zone's icon on the base.
    const boss = bossFor(bossLevel);
    if (boss) drawBossBadge(g, boss, Math.round(w / 2) - 4, 3 * k - 1);
    drawWorldIcon(g, zone, Math.round(w / 2) - 4, 13 * k);
    if (!reducedMotion()) {
      cup.setScale(0.2);
      this.tweens.add({ targets: cup, scale: 1, duration: 520, ease: 'Back.easeOut' });
    }
    return h / 2;
  }

  /** A gold medal on a ribbon, a star on it. */
  private medal(cx: number, cy: number): void {
    const g = this.add.graphics({ x: cx, y: cy });
    g.fillStyle(0x3f6fd0).fillRect(-7, -14, 5, 10).fillRect(2, -14, 5, 10);
    g.fillStyle(0xd84a3a).fillRect(-5, -14, 2, 10).fillRect(3, -14, 2, 10);
    g.fillStyle(UI.shadow).fillCircle(1, 2, 10);
    g.fillStyle(GOLD_DARK).fillCircle(0, 1, 10);
    g.fillStyle(GOLD).fillCircle(0, 0, 9);
    g.fillStyle(GOLD_LIGHT).fillCircle(-3, -3, 3);
    drawBigStar(g, -10, -10, true, 3);
    if (!reducedMotion()) {
      g.setScale(0).setAngle(-30);
      this.tweens.add({
        targets: g,
        scale: 1,
        angle: 0,
        delay: 350,
        duration: 420,
        ease: 'Back.easeOut',
      });
    }
  }

  /** A prize rosette: a ribboned badge with a star (milestones). */
  private rosette(cx: number, cy: number): number {
    const g = this.add.graphics({ x: cx, y: cy });
    g.fillStyle(0xd84a3a).fillTriangle(-10, 6, -2, 6, -12, 30).fillTriangle(10, 6, 2, 6, 12, 30);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      g.fillStyle(i % 2 ? GOLD_DARK : GOLD).fillCircle(Math.cos(a) * 14, Math.sin(a) * 14, 6);
    }
    g.fillStyle(UI.panel).fillCircle(0, 0, 12);
    g.fillStyle(GOLD).fillCircle(0, 0, 10);
    drawBigStar(g, -11, -11, true, 3);
    if (!reducedMotion()) {
      g.setScale(0);
      this.tweens.add({ targets: g, scale: 1, angle: 360, duration: 600, ease: 'Back.easeOut' });
    }
    return 22;
  }

  /** A heap of coins, three pixels to the pixel, popping in (the share gift). */
  private coinHeap(cx: number, cy: number): number {
    const k = 3;
    const g = this.add.graphics();
    // Back row first: a pyramid of six, each half over the ones below it.
    const spots = [
      [10, 0],
      [5, 5],
      [15, 5],
      [0, 10],
      [10, 10],
      [20, 10],
    ] as const;
    for (const [x, y] of spots) drawCoin(g, x, y, UI.shadow);
    const w = 28 * k;
    const h = 18 * k;
    g.setScale(k).setPosition(Math.round(cx - w / 2), Math.round(cy - h / 2));
    if (!reducedMotion()) {
      g.setAlpha(0);
      this.tweens.add({
        targets: g,
        alpha: 1,
        y: { from: g.y - 12, to: g.y },
        duration: 380,
        ease: 'Bounce.easeOut',
      });
    }
    return h / 2;
  }

  /** A wrapped present (the rare find before it opens). */
  private giftBox(cx: number, cy: number): Phaser.GameObjects.Graphics {
    const g = this.add.graphics({ x: cx, y: cy });
    g.fillStyle(UI.shadow).fillRect(-15, -11, 32, 28);
    g.fillStyle(0xd84a3a).fillRect(-16, -12, 32, 28);
    g.fillStyle(0xb03a2c).fillRect(-18, -16, 36, 8);
    g.fillStyle(GOLD).fillRect(-3, -16, 6, 32).fillRect(-16, 0, 32, 5);
    g.fillStyle(GOLD).fillTriangle(0, -16, -12, -24, -8, -14).fillTriangle(0, -16, 12, -24, 8, -14);
    return g;
  }
}

/** A zone's name for a button: without a leading "The" or trailing filler, short enough to fit. */
function shortName(name: string): string {
  const words = name.toUpperCase().split(' ');
  return words.length > 2 ? words.slice(-2).join(' ') : words.join(' ');
}

/** Roman numerals for upgrade levels (I to X). */
function roman(n: number): string {
  return ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'][n] ?? String(n);
}

/** Whether an upgrade level is a zone's headline (its big goal). */
function isHeadline(id: UpgradeKind, level: number): boolean {
  return HEADLINE_UPGRADES[id]?.includes(level) ?? false;
}
